import React, { useContext, useState, useLayoutEffect } from "react"
import { ThemeProvider } from "styled-components"
import { fleFormTheme } from "../../../themes/formThemes"
import { TopSection } from "../../views/fle/TopSection"
import Title from "../../views/Title"
import { FleLayout } from "../../views/fle/FleLayout"
import { useRoutingInfo } from "../context/RoutingInfoContext"
import { Container } from "@carrotfertility/carotene"
import { CenteredLoadingIndicator } from "components/views/molecules/Molecules"
import { BackButton, JourneyTopSection } from "./ProgressTracker"
import { useStateMachine } from "components/context/stateMachine/StateMachineContext"
import { Steps } from "../steps/journey/step-library"

type RenderBottomContent = null | (() => () => JSX.Element)
export type JourneyFlowContextProps = {
  setShowBackButton: (showBackButton: boolean) => void
  setShowProgressTracker: (showBackButton: boolean) => void
  setRenderBottomContent: (renderBottomContent: RenderBottomContent) => void
  handleContinueLoading: (onClickContinue: () => Promise<void>) => Promise<void>
  continueLoading: boolean
}

type UseJourneyFlowProps = { bottomContent?: () => JSX.Element }

export const JourneyFlowContext = React.createContext<JourneyFlowContextProps>(null)

export const useJourneyFlow = (props?: UseJourneyFlowProps): JourneyFlowContextProps => {
  const journeyFlowContext = useContext(JourneyFlowContext)
  if (props?.bottomContent) {
    journeyFlowContext.setRenderBottomContent(() => props.bottomContent)
  } else {
    journeyFlowContext.setRenderBottomContent(null)
  }
  return journeyFlowContext
}

const MATCH_STEPS = [Steps.ITS_A_MATCH_URGENT_PROVIDER_PLACEMENT, Steps.ITS_A_MATCH_NON_URGENT]

export function JourneyFlowContainer({ children }): JSX.Element {
  const [renderBottomContent, setRenderBottomContent] = useState(null)
  const { initialLoading } = useRoutingInfo()
  const [showBackButton, setShowBackButton] = useState(true)
  const [showProgressTracker, setShowProgressTracker] = useState(true)
  const { step } = useStateMachine()
  const [journeySection, setJourneySection] = useState("JOURNEY")
  const [continueLoading, setContinueLoading] = useState(false)

  async function handleContinueLoading(onClickContinue: () => Promise<void>): Promise<void> {
    if (!continueLoading) {
      setContinueLoading(true)
      await onClickContinue()
      setContinueLoading(false)
    }
  }

  const context = {
    setShowBackButton,
    setShowProgressTracker,
    setRenderBottomContent,
    handleContinueLoading,
    continueLoading
  }

  useLayoutEffect(() => {
    if (MATCH_STEPS.includes(step.name as Steps)) {
      setJourneySection("MATCH")
    } else {
      setJourneySection("JOURNEY")
    }
  }, [step.name])

  return (
    <ThemeProvider theme={fleFormTheme}>
      <JourneyFlowContext.Provider value={context}>
        <Title title={`Journey`} />
        <FleLayout bottomContent={renderBottomContent && renderBottomContent()} closeButton>
          {initialLoading ? (
            <Container paddingTopBottom="giant">
              <CenteredLoadingIndicator />
            </Container>
          ) : (
            <>
              <JourneyTopSection {...{ showBackButton, showProgressTracker, journeySection }}>
                <TopSection
                  progressStep={4}
                  leftOrBackArrow={showBackButton ? <BackButton /> : null}
                  {...{ showProgressTracker }}
                />
              </JourneyTopSection>
              {children}
            </>
          )}
        </FleLayout>
      </JourneyFlowContext.Provider>
    </ThemeProvider>
  )
}
